"use client";

import Link from "next/link";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabaseClient";

export default function Navbar({ email, isAdmin }: { email?: string; isAdmin?: boolean }) {
  const router = useRouter();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    router.push("/login");
  };

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="glass sticky top-0 z-50 border-b border-border"
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <Link href="/" className="group flex items-center gap-0.5" aria-label="ClearCut home">
          <span className="font-display text-xl font-bold tracking-tight text-white">
            Clear
          </span>
          <span className="font-display text-xl font-bold tracking-tight text-accent">
            Cut
          </span>
          <motion.span
            className="ml-1.5 inline-block h-5 w-[2px] bg-accent"
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
          />
        </Link>

        <div className="flex items-center gap-5">
          {email ? (
            <>
              <Link
                href="/dashboard"
                className="group relative text-sm font-body text-muted transition-colors hover:text-white"
              >
                Dashboard
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
              </Link>
              <Link
                href="/pricing"
                className="group relative text-sm font-body text-muted transition-colors hover:text-white"
              >
                Pricing
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
              </Link>
              <Link
                href="/analytics"
                className="group relative text-sm font-body text-muted transition-colors hover:text-white"
              >
                Analytics
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
              </Link>
              {isAdmin && (
                <Link
                  href="/admin"
                  className="group relative text-sm font-body text-muted transition-colors hover:text-white"
                >
                  Admin
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
                </Link>
              )}
              <span className="text-sm text-muted-dim">{email}</span>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleLogout}
                aria-label="Log out"
                className="rounded-md border border-border px-3 py-1.5 text-sm text-muted transition-colors hover:border-border-light hover:text-white"
              >
                Log out
              </motion.button>
            </>
          ) : (
            <>
              <Link
                href="/pricing"
                className="group relative text-sm font-body text-muted transition-colors hover:text-white"
              >
                Pricing
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
              </Link>
              <Link href="/login">
                <motion.span
                  whileHover={{ scale: 1.03, boxShadow: "0 0 20px rgba(200,255,0,0.2)" }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-block rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-black"
                >
                  Sign in
                </motion.span>
              </Link>
            </>
          )}
        </div>
      </div>
    </motion.nav>
  );
}
